import { css } from "@compiled/react";
import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";

import {
    ListOfLinks,
    ListOfLinksProps,
    ListOfLinksHeaderText
} from "../../../components/global-nav/list-of-links/src";
import {
    SolutionsCard,
    SolutionsCardProps
} from "../../../components/global-nav/solutions-card/src";
import Link from "../../../components/wpl/link/src";

// ==================================================================
// Type Props
// ==================================================================
export interface SolutionsDropdownProps {
    productKey: ProductKeyTypes;
    className?: string;
}

// ==================================================================
// Styles
// ==================================================================
const atlassianTogetherStyle = css({
    "textDecoration": "none",
    "color": "#091e42",
    "&:hover": {
        ".atlassiantogether": {
            color: "#0065ff"
        }
    },
    "&:visited": {
        color: "#091e42"
    }
});

const solutionsCardRowStyle = css({
    marginBlockStart: token("space.250"),
    [media.above.sm]: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr"
    }
});

const solutionsCardStyle = css({
    marginBlockEnd: token("space.150"),
    [media.above.sm]: {
        marginInlineEnd: token("space.250"),
        display: "flex"
    }
});

const listOfLinksRowStyle = css({
    marginBlockStart: token("space.400"),
    display: "grid",
    [media.above.sm]: {
        gridTemplateColumns: "1fr 1fr 1fr"
    },
    [media.below.sm]: {
        gridTemplateColumns: "1fr"
    }
});

const listOfLinksStyle = css({
    marginBlockEnd: token("space.200")
});

// ==================================================================
// Rendering Functions
// ==================================================================
const FormatAtlassianTogether = (productKey: ProductKeyTypes) => {
    const intl = useIntl();

    const descriptionText = (
        <FormattedMessage
            id="solutions-dropdown.sidebar.at-description"
            defaultMessage="Get Atlassian work management products in one convenient package for enterprise teams."
            values={DEFAULT_VALUES}
        />
    );

    const learnMoreText = intl.formatMessage({
        id: "solutions-dropdown.sidebar.learn-more",
        defaultMessage: "Learn more"
    });

    return (
        <div
            css={css({
                marginBlockStart: token("space.150"),
                marginBlockEnd: token("space.600")
            })}
        >
            <a
                href={useLocalizedHref("/solutions/work-management/together")}
                css={atlassianTogetherStyle}
                data-event="clicked"
                data-event-component="linkButton"
                data-event-container="globalNavWacDropdownContainer"
            >
                <div
                    css={css({
                        display: "block"
                    })}
                >
                    <div
                        className="atlassiantogether"
                        css={css({
                            fontSize: token("space.200"),
                            marginBlockEnd: token("space.100"),
                            marginBlockStart: token("space.200")
                        })}
                    >
                        {"Atlassian Together"}
                    </div>
                    <div
                        css={css({
                            color: "#505f79",
                            fontSize: token("space.150"),
                            marginBlockEnd: token("space.075")
                        })}
                    >
                        <p
                            css={css({
                                maxWidth: "270px"
                            })}
                        >
                            {descriptionText}
                        </p>
                    </div>
                    <Link
                        css={css({
                            "&:hover, &:focus": {
                                textShadow: "none"
                            }
                        })}
                        text={learnMoreText}
                        href={useLocalizedHref(
                            "/solutions/work-management/together"
                        )}
                        type="basic"
                        customLinkColor="#0052cc"
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event-component": "linkButton",
                            "data-event-container":
                                "globalNavWacDropdownContainer",
                            "data-event": "clicked"
                        }}
                    />
                </div>
            </a>
        </div>
    );
};

export const SolutionsMain = ({ className }: SolutionsDropdownProps) => {
    const intl = useIntl();

    const mainTitle = intl.formatMessage({
        id: "solutions-dropdown.main.title",
        defaultMessage: "Featured"
    });

    const workManagementCard: SolutionsCardProps = {
        href: useLocalizedHref("/solutions/work-management"),
        title: intl.formatMessage({
            id: "solutions-dropdown.main.wm-title",
            defaultMessage: "Work Management"
        }),
        body: intl.formatMessage({
            id: "solutions-dropdown.main.wm-body",
            defaultMessage:
                "Manage projects and align goals across all teams to achieve deliverables"
        }),
        icon: "pagemultiple",
        gradient: {
            firstColor: "#b2d4ff",
            secondColor: "#eae6ff"
        }
    };

    const itServiceManagementCard: SolutionsCardProps = {
        href: useLocalizedHref("/solutions/itsm"),
        title: intl.formatMessage({
            id: "solutions-dropdown.main.itsm-title",
            defaultMessage: "IT Service Management"
        }),
        body: intl.formatMessage({
            id: "solutions-dropdown.main.itsm-body",
            defaultMessage:
                "Enable dev, IT ops, and business teams to deliver great service at high velocity"
        }),
        icon: "bold",
        gradient: {
            firstColor: "#b2d4ff",
            secondColor: "#e3fcef"
        }
    };

    const agileDevOpsCard: SolutionsCardProps = {
        href: useLocalizedHref("/solutions/devops"),
        title: intl.formatMessage({
            id: "solutions-dropdown.main.devops-title",
            defaultMessage: "Agile & DevOps"
        }),
        body: intl.formatMessage({
            id: "solutions-dropdown.main.devops-body",
            defaultMessage:
                "Run a world-class agile software organization from discovery to delivery and operations"
        }),
        icon: "diagramorgchart",
        gradient: {
            firstColor: "#eae6ff",
            secondColor: "#ffebe5"
        }
    };

    const teamSizeListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "solutions-dropdown.main.team-size",
            defaultMessage: "By team size"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/enterprise"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.enterprise",
                    defaultMessage: "Enterprise"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/software/small-business"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.sb",
                    defaultMessage: "Small Business"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/software/startups"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.startup",
                    defaultMessage: "Startup"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/teams/nonprofits"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.nonprofit",
                    defaultMessage: "Non-profit"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    const teamFunctionListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "solutions-dropdown.main.team-function",
            defaultMessage: "By team function"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/teams/software-development"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.sd",
                    defaultMessage: "Software Development"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/teams/it"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.it",
                    defaultMessage: "IT"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/teams/finance"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.finance",
                    defaultMessage: "Finance"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/teams/marketing"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.marketing",
                    defaultMessage: "Marketing"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/teams/hr"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.hr",
                    defaultMessage: "HR"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    const industryListProps: ListOfLinksProps = {
        isSideBar: false,
        headerText: intl.formatMessage({
            id: "solutions-dropdown.main.industry",
            defaultMessage: "By industry"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/industries/retail"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.retail",
                    defaultMessage: "Retail"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/industries/telecom"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.telecom",
                    defaultMessage: "Telecommunications"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/industries/professional-services"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.ps",
                    defaultMessage: "Professional Services"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/government"),
                isHaveIcon: false,
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.main.government",
                    defaultMessage: "Government"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    return (
        <div
            className={className}
            css={css({
                width: "100%",
                marginBlockStart: token("space.300")
            })}
        >
            <div>
                <ListOfLinksHeaderText
                    isSideBar={false}
                    headerText={mainTitle}
                />
                <div css={solutionsCardRowStyle}>
                    <div css={solutionsCardStyle}>
                        <SolutionsCard {...workManagementCard} />
                    </div>
                    <div css={solutionsCardStyle}>
                        <SolutionsCard {...itServiceManagementCard} />
                    </div>
                    <div css={solutionsCardStyle}>
                        <SolutionsCard {...agileDevOpsCard} />
                    </div>
                </div>
            </div>
            <div css={listOfLinksRowStyle}>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...teamSizeListProps} />
                </div>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...teamFunctionListProps} />
                </div>
                <div css={listOfLinksStyle}>
                    <ListOfLinks {...industryListProps} />
                </div>
            </div>
        </div>
    );
};

export const SolutionsSideBar = ({
    className,
    productKey
}: SolutionsDropdownProps) => {
    const intl = useIntl();
    const sideBarTitle = intl.formatMessage({
        id: "solutions-dropdown.sidebar.title",
        defaultMessage: "What's new"
    });

    const listOfLinksProps: ListOfLinksProps = {
        isSideBar: true,
        headerText: intl.formatMessage({
            id: "solutions-dropdown.sidebar.helpful",
            defaultMessage: "You might find helpful"
        }),
        listOfLinkWithProps: [
            {
                href: useLocalizedHref("/trust"),
                isHaveIcon: true,
                iconName: "securitycheckmark",
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.sidebar.trust",
                    defaultMessage: "Atlassian Trust & Security"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            },
            {
                href: useLocalizedHref("/customers"),
                isHaveIcon: true,
                iconName: "building",
                linkText: intl.formatMessage({
                    id: "solutions-dropdown.sidebar.customers",
                    defaultMessage: "Customer Case Studies"
                }),
                dataEventContainer: "globalNavWacDropdownContainer"
            }
        ]
    };

    return (
        <div className={className}>
            <ListOfLinksHeaderText
                isSideBar={false}
                headerText={sideBarTitle}
            />
            {FormatAtlassianTogether(productKey)}
            <ListOfLinks {...listOfLinksProps} />
        </div>
    );
};
